import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Clock4, Moon, Sun, User, Video } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import { useTheme } from '../lib/theme';
import { Avatar } from './Avatar';
import { MyPresencePicker } from './MyPresencePicker';
import { useRec } from './rec/RecProvider';

/**
 * TODO LO PERSONAL, DEBAJO DEL AVATAR.
 *
 * El idioma (EN/ES), el tema y la disponibilidad vivían sueltos en la barra de arriba y se comían
 * el ancho: con la pantalla dividida —que es como trabaja el dueño— los badges de Messaging y
 * Notifications quedaban fuera de la vista y había que maximizar la ventana, mirar, y volver a
 * minimizar. Son ajustes que se tocan una vez por semana; los contadores se miran todo el día, así
 * que la barra es de ellos.
 *
 * Se abre con click (no con hover): en pantalla dividida el mouse pasa por encima todo el tiempo.
 */
export function ProfileMenu({ userId, name }: { userId: string; name: string | null }) {
  const { t, lang, setLang } = useI18n();
  const { theme, toggle: toggleTheme } = useTheme();
  const rec = useRec();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  const fila: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-xs)',
    padding: '7px 10px', borderRadius: 8, width: '100%',
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex', marginLeft: 2 }}>
      <button type="button" onClick={() => setOpen((v) => !v)} title={t('prof.myProfile')}
        aria-haspopup="menu" aria-expanded={open}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', borderRadius: '50%' }}>
        <Avatar userId={userId} name={name} size={30} />
      </button>

      {open && (
        <div role="menu" className="card"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 500,
            width: 232, margin: 0, padding: 8, display: 'grid', gap: 2,
            boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
          }}>
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], padding: '2px 10px 4px' }}>
            {name ?? ''}
          </div>

          {/* Idioma: los dos visibles, no un toggle — se ve cuál está activo sin abrir nada más. */}
          <div style={{ ...fila, justifyContent: 'space-between' }}>
            <span>{t('prof.language')}</span>
            <span style={{ display: 'inline-flex', gap: 4 }}>
              {(['en', 'es'] as const).map((l) => (
                <button key={l} onClick={() => setLang(l)} className={`btn ghost sm${lang === l ? ' active' : ''}`}
                  style={{ fontSize: 'var(--fs-2xs)', padding: '3px 8px', ...(lang === l ? { background: 'var(--accent-soft)', borderColor: 'var(--accent-2)' } : {}) }}>
                  {l.toUpperCase()}
                </button>
              ))}
            </span>
          </div>

          <button type="button" onClick={toggleTheme} className="btn ghost sm"
            style={{ ...fila, justifyContent: 'space-between', border: 'none' }}>
            <span>{theme === 'dark' ? t('theme.toLight') : t('theme.toDark')}</span>
            {theme === 'dark' ? <Sun size={15} strokeWidth={2} /> : <Moon size={15} strokeWidth={2} />}
          </button>

          {/* La disponibilidad se escribe en RingCentral: el picker trae su propia lógica y se
              esconde solo si el usuario no tiene extensión enlazada. */}
          <div style={{ ...fila, justifyContent: 'space-between' }}>
            <span>{t('prof.availability')}</span>
            <MyPresencePicker />
          </div>

          <div style={{ height: 1, background: 'var(--line)', margin: '4px 6px' }} />

          <NavLink to={`/employee/${userId}`} onClick={() => setOpen(false)} className="btn ghost sm"
            style={{ ...fila, border: 'none', textDecoration: 'none' }}>
            <User size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('prof.viewProfile')}
          </NavLink>
          <NavLink to="/my-hours" onClick={() => setOpen(false)} className="btn ghost sm"
            style={{ ...fila, border: 'none', textDecoration: 'none' }}>
            <Clock4 size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('clock.myHours')}
          </NavLink>
          {/* Record (spec §4.12.5): mis grabaciones, con la sin terminar para recuperar o descartar. */}
          {rec.canRecord && (
            <button type="button" onClick={() => { setOpen(false); rec.openMine(); }} className="btn ghost sm"
              style={{ ...fila, border: 'none', justifyContent: 'flex-start' }}>
              <Video size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('rec.mine')}
              {rec.recovery && <span className="badge orange" style={{ marginLeft: 'auto', fontSize: 'var(--fs-2xs)' }}>1</span>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
